import React, { useEffect, useState, lazy, Suspense } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Home } from 'lucide-react';
import { BACKEND_URL } from '../utils/api';

/* ═══════════════════════════════════════════════════════════════════
   SHARED MUSEUM ROOM — public 3D viewer (no login required).
   Route: /share/museum/:roomId/:userId

   Reads sender's memories from GET /api/share/museum/{room}/{user_id}
   and renders the same 3D `MuseumScenePure` the owner uses — pre-
   loaded with the sender's `items` so the visitor sees a real 3D hall
   they can rotate / walk through.

   Read-only by design: this route is unauthenticated, so any write
   action (upload, edit, react) is naturally blocked by the backend's
   token requirement.  We also pass `publicData` to MuseumScenePure so
   it skips the auth-gated /api/memories fetch.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD     = '#D4AF37';
const GOLD_HI  = '#F5D97A';

const MuseumLazy = lazy(() => import('../components/museum/MuseumScenePure'));

export default function SharedMuseumRoom() {
  const { roomId, userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/share/museum/${roomId}/${userId}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Hall not found'); });
    return () => { cancelled = true; };
  }, [roomId, userId]);

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center text-center px-6"
        style={{ background: '#04030a', color: GOLD_HI, fontFamily: 'Cormorant Garamond, serif' }}
        data-testid="shared-museum-error">
        <div>
          <h1 className="text-3xl italic mb-2">The hall is sealed.</h1>
          <p className="text-white/50">{String(error)}</p>
          <button onClick={() => navigate('/welcome')}
            className="mt-6 w-12 h-12 rounded-full inline-flex items-center justify-center active:scale-95 transition-transform"
            style={{ border: `1px solid ${GOLD}`, color: GOLD_HI, background: 'rgba(0,0,0,0.5)' }}
            aria-label="Home"
            data-testid="shared-museum-error-home">
            <Home size={20} />
          </button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center"
        style={{ background: '#04030a', color: GOLD_HI, fontFamily: 'Cormorant Garamond, serif' }}
        data-testid="shared-museum-loading">
        <div className="flex flex-col items-center gap-3">
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: `2px solid ${GOLD}`, borderTopColor: 'transparent',
            animation: 'museumSpin 0.8s linear infinite',
          }} />
          <style>{`@keyframes museumSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          <p className="italic text-sm">summoning the hall…</p>
        </div>
      </div>
    );
  }

  /* The 3D scene fills the screen. A small floating "Home" pill at the
     top-left lets the visitor exit. */
  return (
    <div className="fixed inset-0" style={{ background: '#04030a' }}
      data-testid="shared-museum-3d">
      <Suspense fallback={
        <div className="fixed inset-0 flex items-center justify-center"
          style={{ background: '#04030a', color: GOLD_HI, fontFamily: 'Cormorant Garamond, serif' }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: `2px solid ${GOLD}`, borderTopColor: 'transparent',
            animation: 'museumSpin 0.8s linear infinite',
          }} />
        </div>
      }>
        <MuseumLazy
          onExit={() => navigate('/welcome')}
          initialRoomId={roomId}
          publicData={data}
        />
      </Suspense>

      {/* Floating owner plaque — top-left */}
      <div
        className="fixed top-0 left-0 right-0 flex items-center justify-between px-4 py-3 pointer-events-none"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 10px)',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.65), transparent)',
          color: GOLD_HI,
          fontFamily: 'Cormorant Garamond, Georgia, serif',
          zIndex: 20,
        }}>
        <button
          type="button"
          onClick={() => navigate('/welcome')}
          className="pointer-events-auto w-11 h-11 rounded-full inline-flex items-center justify-center active:scale-90 transition-transform"
          style={{
            border: `1px solid ${GOLD}88`,
            background: 'rgba(0,0,0,0.55)',
            color: GOLD_HI,
            backdropFilter: 'blur(6px)',
            boxShadow: `0 0 14px ${GOLD}33`,
          }}
          aria-label="Home"
          data-testid="shared-museum-home-btn">
          <Home size={18} />
        </button>
        <div className="text-center">
          <p className="italic text-lg leading-none"
            style={{ textShadow: '0 0 12px rgba(212,175,55,0.4)' }}>
            {data?.meta?.label || 'Hall'}
          </p>
          <p className="font-['Outfit'] uppercase tracking-[0.24em] text-[9px] mt-1"
            style={{ color: `${GOLD_HI}aa` }}>
            of {data?.user?.nickname || data?.user?.email || 'the Sovereign'}
          </p>
        </div>
        <div className="w-[68px]" />
      </div>
    </div>
  );
}
